'use client';

import Link from 'next/link';

import { motion } from 'framer-motion';
import { Home, Search, ArrowUpRight, HelpCircle } from 'lucide-react';

export default function NotFound() {
  return (
    <div className="min-h-screen w-full bg-background overflow-hidden">
      <div className="relative flex items-center justify-center min-h-screen p-4">
        {/* Premium Background */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Base grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(var(--primary-rgb),0.02)_1px,transparent_1px),linear-gradient(to_right,rgba(var(--primary-rgb),0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
          
          {/* Gradient orbs */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-[600px] h-[600px] rounded-full bg-gradient-to-br from-primary/3 to-primary/3 blur-[100px]"
              style={{
                top: `${20 + i * 30}%`,
                left: `${10 + i * 40}%`,
              }}
              animate={{
                y: [0, -30, 0],
                x: [0, 20, 0],
                scale: [1, 1.1, 1],
                rotate: [0, 45, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Light overlay */}
          <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px]" />
        </div>

        <div className="w-full max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative bg-card/5 backdrop-blur-xl rounded-2xl p-6 sm:p-12 border border-primary/10"
          >
            <div className="text-center">
              {/* 404 Text */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-12"
              >
                <h1 className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary 
                             bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
                  404
                </h1>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Page Not Found
                </h2>
                <p className="text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
                  The page you're looking for seems to have vanished into thin air. 
                  No worries though – let's get you back on track! 🚀
                </p>
              </motion.div>

              {/* Quick Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12"
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    href="/"
                    className="flex items-center justify-center gap-3 px-8 py-4 rounded-xl 
                             bg-primary text-primary-foreground font-medium shadow-lg shadow-primary/20 
                             hover:shadow-xl hover:shadow-primary/30 transition-all duration-200 group"
                  >
                    <Home className="w-5 h-5" />
                    <span>Return Home</span>
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" />
                  </Link>
                </motion.div>
                
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    href="/support"
                    className="flex items-center justify-center gap-3 px-8 py-4 rounded-xl 
                             bg-card/5 text-foreground font-medium border border-primary/10
                             hover:bg-card/10 transition-all duration-200 group"
                  >
                    <HelpCircle className="w-5 h-5" />
                    <span>Get Support</span>
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Popular Pages */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-center"
              >
                <h3 className="text-sm font-medium text-primary mb-4">
                  Popular Destinations
                </h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {[
                    { label: 'Mobile App', href: '/features' },
                    { label: 'Pricing', href: '/pricing' },
                    { label: 'Blog', href: '/blog' },
                  ].map((link, index) => (
                    <motion.div
                      key={link.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      whileHover={{ y: -2 }}
                    >
                      <Link
                        href={link.href}
                        className="px-6 py-2 rounded-full text-sm bg-card/5 border border-primary/10 
                                 text-muted-foreground hover:text-primary hover:border-primary/30 transition-all duration-200"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
} 